/*
  Love2Stick — Glass & Motion controller.
  Vanilla JS, no dependencies. Defensive: every feature checks for support
  and fails silently (content stays visible / unanimated) rather than breaking the page.
*/
(function () {
  'use strict';

  var html = document.documentElement;

  var config = {
    glass: true,
    motion: true,
    reveal: true,
    parallax: true
  };

  try {
    var configEl = document.getElementById('l2s-motion-config');
    if (configEl && configEl.textContent) {
      var parsed = JSON.parse(configEl.textContent);
      for (var key in parsed) {
        if (Object.prototype.hasOwnProperty.call(parsed, key)) {
          config[key] = parsed[key];
        }
      }
    }
  } catch (e) {
    /* keep defaults */
  }

  var reducedMotion = false;
  try {
    reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  if (config.glass) html.classList.add('l2s-glass-on');
  if (config.motion && !reducedMotion) html.classList.add('l2s-motion-on');
  if (config.reveal && !reducedMotion) html.classList.add('l2s-reveal-ready');
  if (config.parallax && !reducedMotion) html.classList.add('l2s-parallax-on');

  /* ------------------------------------------------------------------ */
  /* Hero entrance cleanup                                                */
  /* The product card's one-time load-in animation and the scroll-pin    */
  /* sequence's own opacity/transform rule both target .l2s-hero__card.  */
  /* Once the entrance finishes, clear its inline animation so the       */
  /* scroll-linked rule (or the plain resting style) takes over cleanly  */
  /* instead of being permanently pinned by the entrance's forwards-fill.*/
  /* ------------------------------------------------------------------ */
  function initHeroEntranceCleanup() {
    var card = document.querySelector('.l2s-hero__card');
    if (!card) return;
    card.addEventListener('animationend', function handler(event) {
      if (event.target !== card) return;
      card.style.animation = 'none';
      card.removeEventListener('animationend', handler);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Hero shape carousel (circle / square)                               */
  /* Pure progressive enhancement: with no JS, slide 0 (circle) is the   */
  /* only visible image via the CSS default. Auto-advances on a timer,   */
  /* pauses out of view / on hover / on focus, and stays fully navigable */
  /* via the prev/next arrows, which also reset the auto-advance timer.  */
  /* Reduced motion disables the timer only — arrows remain interactive  */
  /* so a visitor can still switch shapes manually.                     */
  /* ------------------------------------------------------------------ */
  function initHeroCarousel() {
    var carousels = document.querySelectorAll('[data-l2s-hero-carousel]');
    if (!carousels.length) return;

    var AUTO_ADVANCE_MS = 1500;

    carousels.forEach(function (carousel) {
      var root = carousel.closest('.l2s-hero__media-frame') || carousel.parentElement;
      var slides = Array.prototype.slice.call(carousel.querySelectorAll('[data-l2s-slide]'));
      if (slides.length < 2) return;

      var prevBtn = root ? root.querySelector('[data-l2s-hero-arrow="prev"]') : null;
      var nextBtn = root ? root.querySelector('[data-l2s-hero-arrow="next"]') : null;

      var activeIndex = 0;
      var timerId = null;
      var inView = false;
      var hovered = false;

      function setActive(index) {
        activeIndex = index;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
      }

      function stopTimer() {
        if (timerId) {
          window.clearTimeout(timerId);
          timerId = null;
        }
      }

      function scheduleNext() {
        stopTimer();
        if (!inView || hovered || reducedMotion) return;
        timerId = window.setTimeout(function () {
          setActive((activeIndex + 1) % slides.length);
          scheduleNext();
        }, AUTO_ADVANCE_MS);
      }

      function goTo(index) {
        setActive((index + slides.length) % slides.length);
        scheduleNext();
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(activeIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(activeIndex + 1);
        });
      }

      if (root) {
        root.addEventListener('mouseenter', function () {
          hovered = true;
          stopTimer();
        });
        root.addEventListener('mouseleave', function () {
          hovered = false;
          scheduleNext();
        });
        root.addEventListener('focusin', function () {
          hovered = true;
          stopTimer();
        });
        root.addEventListener('focusout', function () {
          hovered = false;
          scheduleNext();
        });
      }

      if (typeof IntersectionObserver !== 'undefined') {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              inView = entry.isIntersecting;
              if (inView) {
                scheduleNext();
              } else {
                stopTimer();
              }
            });
          },
          { threshold: 0.3 }
        );
        io.observe(carousel);
      } else {
        inView = true;
        scheduleNext();
      }

      setActive(0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll-driven hero                                                   */
  /* Pure progressive enhancement: the hero is a complete, legible,      */
  /* static section without this. Desktop + motion-ok only; the pinned  */
  /* scroll distance is created by adding a class, never by CSS alone,  */
  /* so a JS failure never leaves a tall dead-space gap.                */
  /* ------------------------------------------------------------------ */
  function initScrollHero() {
    if (reducedMotion) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.innerWidth < 750) return;

    var heroes = document.querySelectorAll('.l2s-hero[data-scroll-sequence="true"]');
    if (!heroes.length) return;

    heroes.forEach(function (hero) {
      var pinVh = parseInt(hero.getAttribute('data-pin-vh'), 10);
      if (!pinVh || pinVh < 120) pinVh = 200;
      hero.style.setProperty('--l2s-pin-vh', pinVh);

      var ticking = false;
      var listening = false;

      function update() {
        ticking = false;
        var rect = hero.getBoundingClientRect();
        var viewportH = window.innerHeight;
        var total = rect.height - viewportH;
        if (total <= 0) return;
        var scrolled = -rect.top;
        var progress = Math.max(0, Math.min(1, scrolled / total));
        hero.style.setProperty('--l2s-hero-progress', progress.toFixed(4));

        var cardProgress = Math.max(0, Math.min(1, (progress - 0.18) / 0.32));
        hero.style.setProperty('--l2s-hero-card-progress', cardProgress.toFixed(4));

        var cueOpacity = Math.max(0, 1 - progress * 10);
        hero.style.setProperty('--l2s-scrollcue-opacity', cueOpacity.toFixed(2));
      }

      function onScroll() {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      }

      function startListening() {
        if (listening) return;
        listening = true;
        hero.classList.add('l2s-hero--enhanced');
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update();
      }

      function stopListening() {
        if (!listening) return;
        listening = false;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            startListening();
          } else {
            stopListening();
          }
        });
      });
      io.observe(hero);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scroll reveal                                                       */
  /* Section wrapper ids rendered from a JSON template carry a numeric   */
  /* template prefix (shopify-section-template--N__key), so we match by  */
  /* suffix rather than assuming the bare key. Elements are toggled      */
  /* in both directions (not unobserved) so effects replay whenever a    */
  /* visitor scrolls away and back.                                      */
  /* ------------------------------------------------------------------ */
  function initSectionReveal() {
    if (!html.classList.contains('l2s-reveal-ready')) return;
    if (typeof IntersectionObserver === 'undefined') return;

    function bySuffix(key) {
      return document.querySelector('[id$="__' + key + '"]');
    }

    function one(root, sel) {
      return root ? root.querySelector(sel) : null;
    }

    function all(root, sel) {
      return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
    }

    /* Group-type blocks render as an outer .group-block wrapping an inner
       .group-block-content that actually holds the child blocks — dive
       through that layer so ':scope >' child lookups find real children. */
    function contentOf(groupEl) {
      if (!groupEl) return groupEl;
      var inner = groupEl.querySelector(':scope > .group-block-content');
      return inner || groupEl;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('l2s-is-visible', entry.isIntersecting);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    function reveal(el, delayIndex, variant) {
      if (!el) return;
      el.classList.add(variant || 'l2s-reveal');
      if (delayIndex) el.style.setProperty('--l2s-reveal-delay', Math.min(delayIndex, 6) * 90 + 'ms');
      observer.observe(el);
    }

    function maskReveal(el) {
      if (!el || el.getAttribute('data-l2s-masked') === 'true') return;
      el.setAttribute('data-l2s-masked', 'true');
      var inner = document.createElement('span');
      inner.className = 'l2s-mask-inner';
      while (el.firstChild) inner.appendChild(el.firstChild);
      el.appendChild(inner);
      el.classList.add('l2s-mask');
      observer.observe(el);
    }

    function staggerGroup(container, childSelector, startIndex) {
      if (!container) return;
      all(container, childSelector).forEach(function (child, i) {
        reveal(child, (startIndex || 0) + i);
      });
    }

    /* Trust strip banner */
    reveal(bySuffix('trust_strip'));

    /* Benefits — heading masks in, the four cards cascade as a group */
    var benefits = bySuffix('benefits');
    if (benefits) {
      maskReveal(one(benefits, 'h2'));
      staggerGroup(one(benefits, '.layout-panel-flex--row'), ':scope > .group-block');
    }

    /* Use cases — heading masks in, sub fades, each row of photo cards
       cascades independently as it enters, with a gentle scale-in on
       the photography itself */
    var usecases = bySuffix('usecases');
    if (usecases) {
      var ucWrap = one(usecases, '.section-content-wrapper');
      maskReveal(one(usecases, 'h2'));
      reveal(one(ucWrap, ':scope > .text-block:nth-child(2)'));
      all(usecases, '.layout-panel-flex--row').forEach(function (row) {
        all(row, ':scope > .group-block').forEach(function (card, i) {
          reveal(card, i);
          var img = one(card, '.image-block__image');
          if (img) reveal(img, i, 'l2s-reveal--scale');
        });
      });
    }

    /* How it works — feature photo scales in, steps cascade in turn */
    var how = bySuffix('how_it_works');
    if (how) {
      var howImg = one(how, '.image-block__image');
      if (howImg) reveal(howImg, 0, 'l2s-reveal--fade');
      var howCol = one(how, '.layout-panel-flex--row > .group-block');
      if (howCol) {
        maskReveal(one(howCol, 'h2'));
        staggerGroup(contentOf(howCol), ':scope > .group-block');
      }
    }

    /* Spotlight — packaging photo scales in, checklist cascades,
       price + CTA settle in last */
    var spot = bySuffix('spotlight');
    if (spot) {
      var spotImg = one(spot, '.image-block__image');
      if (spotImg) reveal(spotImg, 0, 'l2s-reveal--fade');
      var spotCol = one(spot, '.layout-panel-flex--row > .group-block');
      if (spotCol) {
        var spotContent = contentOf(spotCol);
        maskReveal(one(spotContent, 'h2'));
        var groups = all(spotContent, ':scope > .group-block');
        reveal(one(spotContent, ':scope > .text-block.rte'));
        staggerGroup(contentOf(groups[0]), ':scope > .group-block');
        reveal(groups[1], 4);
      }
    }

    /* FAQ teaser */
    var faq = bySuffix('faq_teaser');
    if (faq) {
      maskReveal(one(faq, 'h2'));
      reveal(one(faq, '.text-block.rte'), 1);
      reveal(one(faq, 'a.button-custom'), 2);
    }
  }

  /* ------------------------------------------------------------------ */
  /* See It in Action — CSS motion treatment                             */
  /* Starts only while the section is on screen, pauses when it leaves,  */
  /* and offers a manual pause/play toggle. Pure progressive             */
  /* enhancement: with no JS the section is a complete static photo.     */
  /* ------------------------------------------------------------------ */
  function initActionDemo() {
    var sections = document.querySelectorAll('.l2s-action');
    if (!sections.length) return;

    var AUTO_ADVANCE_MS = 3400;

    sections.forEach(function (section) {
      var stepper = section.querySelector('[data-l2s-action-stepper]');
      if (!stepper) return;

      var steps = Array.prototype.slice.call(stepper.querySelectorAll('[data-l2s-step]'));
      var panels = Array.prototype.slice.call(section.querySelectorAll('[data-l2s-panel]'));
      if (!steps.length || !panels.length) return;

      var activeIndex = 0;
      var timerId = null;
      var userPaused = false;
      var inView = false;

      function setActive(index) {
        activeIndex = index;
        steps.forEach(function (step, i) {
          var isActive = i === index;
          step.classList.toggle('is-active', isActive);
          step.setAttribute('aria-selected', String(isActive));
          var fill = step.querySelector('.l2s-action__step-progress-fill');
          if (!fill) return;
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        });
        panels.forEach(function (panel, i) {
          panel.classList.toggle('is-active', i === index);
        });
      }

      function stopTimer() {
        if (timerId) {
          window.clearTimeout(timerId);
          timerId = null;
        }
      }

      function scheduleNext() {
        stopTimer();
        if (userPaused || !inView || reducedMotion) return;
        timerId = window.setTimeout(function () {
          setActive((activeIndex + 1) % steps.length);
          scheduleNext();
        }, AUTO_ADVANCE_MS);
      }

      steps.forEach(function (step, i) {
        step.addEventListener('click', function () {
          setActive(i);
          scheduleNext();
        });
      });

      var toggle = section.querySelector('[data-l2s-action-toggle]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          toggle.setAttribute('aria-pressed', String(!userPaused));
          var textEl = toggle.querySelector('[data-l2s-toggle-text]');
          var iconEl = toggle.querySelector('[data-l2s-toggle-icon]');
          if (textEl) textEl.textContent = userPaused ? 'Replay' : 'Pause';
          if (iconEl) iconEl.textContent = userPaused ? '▶' : '❙❙';
          section.classList.toggle('l2s-action--paused', userPaused);
          if (userPaused) {
            stopTimer();
          } else {
            scheduleNext();
          }
        });
      }

      if (typeof IntersectionObserver !== 'undefined') {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              inView = entry.isIntersecting;
              section.classList.toggle('l2s-action--in-view', inView);
              if (inView) {
                scheduleNext();
              } else {
                stopTimer();
              }
            });
          },
          { threshold: 0.3 }
        );
        io.observe(section);
      } else {
        inView = true;
        section.classList.add('l2s-action--in-view');
        scheduleNext();
      }

      setActive(0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Parallax (lifestyle imagery outside the hero)                       */
  /* ------------------------------------------------------------------ */
  function initParallax() {
    if (!html.classList.contains('l2s-parallax-on')) return;

    var targets = [];
    var howImg = document.querySelector('[id$="__how_it_works"] .image-block__image');
    if (howImg) targets.push({ el: howImg, strength: 0.06 });

    var spotImg = document.querySelector('[id$="__spotlight"] .image-block__image');
    if (spotImg) targets.push({ el: spotImg, strength: 0.06 });

    if (!targets.length) return;

    var ticking = false;

    function update() {
      ticking = false;
      var viewportH = window.innerHeight || document.documentElement.clientHeight;

      targets.forEach(function (t) {
        var rect = t.el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var distanceFromCenter = center - viewportH / 2;
        var offset = Math.max(-24, Math.min(24, -distanceFromCenter * t.strength));
        t.el.style.setProperty('--l2s-parallax-offset', offset.toFixed(1) + 'px');
        if (!t.el.classList.contains('l2s-parallax')) t.el.classList.add('l2s-parallax');
      });
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-cart / form submit micro-feedback                            */
  /* ------------------------------------------------------------------ */
  function initSubmitFeedback() {
    if (!html.classList.contains('l2s-motion-on')) return;

    document.addEventListener(
      'click',
      function (event) {
        var btn = event.target.closest && event.target.closest('button[type="submit"]');
        if (!btn) return;
        btn.classList.remove('l2s-added-pulse');
        /* Force reflow so the animation can restart on repeat clicks */
        void btn.offsetWidth;
        btn.classList.add('l2s-added-pulse');
        window.setTimeout(function () {
          btn.classList.remove('l2s-added-pulse');
        }, 750);
      },
      true
    );
  }

  function start() {
    try {
      initHeroEntranceCleanup();
    } catch (e) {}
    try {
      initHeroCarousel();
    } catch (e) {}
    try {
      initScrollHero();
    } catch (e) {}
    try {
      initSectionReveal();
    } catch (e) {}
    try {
      initActionDemo();
    } catch (e) {}
    try {
      initParallax();
    } catch (e) {}
    try {
      initSubmitFeedback();
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
